import { useEffect, useMemo, useRef, useState } from 'react';
import type { Severity, Style } from '@/lib/postFormatter';
import { LINKEDIN_MAX_CHARS, analyze, applyToSelection, firstLineRange, toPlain } from '@/lib/postFormatter';

const SEVERITY_LABEL: Record<Severity, string> = {
  problem: 'À corriger',
  warning: 'À surveiller',
  ok: 'OK',
};

const STYLES: { style: Style; label: string; sample: string }[] = [
  { style: 'bold', label: 'Gras', sample: '𝐀𝐚' },
  { style: 'italic', label: 'Italique', sample: '𝐴𝑎' },
  { style: 'boldItalic', label: 'Gras italique', sample: '𝑨𝒂' },
  { style: 'plain', label: 'Texte normal', sample: 'Aa' },
];

const SAMPLE = `Pourquoi nos posts du mardi performent mieux

Nous avons testé trois horaires pendant six semaines.

Le créneau de 11 h a doublé les réactions de la première heure.

Le détail : https://exemple.fr/etude

#linkedin #contenu`;

interface Notice {
  tone: 'info' | 'warn';
  text: string;
}

export function PostFormatter() {
  const [text, setText] = useState('');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState<[number, number] | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  const analysis = useMemo(() => (text.trim() ? analyze(text) : null), [text]);

  // Restaure la sélection une fois le texte stylé rendu, pour enchaîner gras puis italique.
  useEffect(() => {
    if (!pending || !ref.current) return;
    ref.current.focus();
    ref.current.setSelectionRange(pending[0], pending[1]);
    setPending(null);
  }, [pending, text]);

  const update = (value: string) => {
    setText(value);
    setCopied(false);
  };

  const apply = (style: Style, range?: [number, number]) => {
    const el = ref.current;
    const [start, end] = range ?? [el?.selectionStart ?? 0, el?.selectionEnd ?? 0];
    if (start === end) {
      setNotice({ tone: 'warn', text: 'Sélectionnez d\'abord des mots dans le texte, puis choisissez un style.' });
      return;
    }
    const result = applyToSelection(text, start, end, style);
    update(result.text);
    setPending([result.start, result.end]);
    const parts: string[] = [];
    if (result.skipped.length > 0) {
      parts.push(`Sans version stylée : ${result.skipped.join(' ')} (laissé tel quel).`);
    }
    if (result.accented > 0) {
      parts.push(`${result.accented} accent${result.accented > 1 ? 's' : ''} recomposé${result.accented > 1 ? 's' : ''} avec un signe combinant : vérifiez le rendu.`);
    }
    setNotice(parts.length > 0 ? { tone: 'warn', text: parts.join(' ') } : null);
  };

  const styleFirstLine = () => {
    const range = firstLineRange(text);
    if (!range) {
      setNotice({ tone: 'warn', text: 'Écrivez d\'abord une première ligne.' });
      return;
    }
    apply('bold', range);
  };

  const copy = (value: string) => {
    try {
      navigator.clipboard
        .writeText(value)
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste silencieusement inerte.
        });
    } catch {
      // Pas de Clipboard API (navigateur ancien, contexte non sécurisé) : on n'interrompt rien.
    }
  };

  const hasStyled = !!analysis && analysis.stats.styled > 0;
  const problems = analysis ? analysis.findings.filter((f) => f.severity === 'problem').length : 0;
  const warnings = analysis ? analysis.findings.filter((f) => f.severity === 'warning').length : 0;

  return (
    <div className="nz-sim nz-fmt">
      <label className="nz-sim__label" htmlFor="nz-fmt-input">
        Écrivez ou collez votre post
      </label>
      <div className="nz-fmt__tools" role="group" aria-label="Style du passage sélectionné">
        {STYLES.map(({ style, label, sample }) => (
          <button
            key={style}
            type="button"
            className="nz-sim__tab nz-fmt__style"
            // Garde la sélection du texte au clic : sans cela le champ la perd avant l'action.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => apply(style)}
          >
            <span className="nz-fmt__sample" aria-hidden="true">
              {sample}
            </span>
            {label}
          </button>
        ))}
        <button
          type="button"
          className="nz-wizard__back nz-fmt__first"
          onMouseDown={(event) => event.preventDefault()}
          onClick={styleFirstLine}
        >
          Première ligne en gras
        </button>
      </div>
      <textarea
        ref={ref}
        id="nz-fmt-input"
        className="nz-sim__input"
        rows={10}
        value={text}
        placeholder="Écrivez votre post, sélectionnez quelques mots, puis cliquez sur un style."
        onChange={(event) => update(event.target.value)}
      />
      <div className="nz-sim__bar">
        <span className={`nz-sim__count${analysis && analysis.stats.chars > LINKEDIN_MAX_CHARS ? ' nz-sim__count--over' : ''}`}>
          {analysis ? analysis.stats.chars : 0} / {LINKEDIN_MAX_CHARS} caractères
        </span>
        <span className="nz-sim__count">{analysis ? `${analysis.stats.styled} stylés` : ''}</span>
        <button
          type="button"
          className="nz-wizard__back nz-sim__sample"
          onClick={() => {
            update(text ? '' : SAMPLE);
            setNotice(null);
          }}
        >
          {text ? 'Effacer' : 'Essayer avec un exemple'}
        </button>
      </div>

      {notice ? (
        <p className={`nz-fmt__notice nz-fmt__notice--${notice.tone}`} role="status">
          {notice.text}
        </p>
      ) : null}

      {analysis ? (
        <div className="nz-sim__result" aria-live="polite">
          <h2 className="nz-sim__summary">
            {problems === 0 && warnings === 0
              ? hasStyled
                ? 'Rien à corriger avant de publier'
                : 'Prêt à mettre en forme'
              : `${problems} point${problems > 1 ? 's' : ''} à corriger, ${warnings} à surveiller`}
          </h2>
          <ul className="nz-sim__findings">
            {analysis.findings.map((finding) => (
              <li key={finding.id} className={`nz-sim__finding nz-sim__finding--${finding.severity}`}>
                <span className="nz-sim__badge">{SEVERITY_LABEL[finding.severity]}</span>
                <strong>{finding.title}</strong>
                <p>{finding.detail}</p>
                {finding.fix ? <p className="nz-sim__fix">→ {finding.fix}</p> : null}
              </li>
            ))}
          </ul>
          <div className="nz-wizard__actions">
            <button type="button" className="nz-btn-primary" onClick={() => copy(text)}>
              {copied ? 'Copié' : 'Copier le post'}
            </button>
            {hasStyled ? (
              <button type="button" className="nz-btn-secondary" onClick={() => copy(toPlain(text))}>
                Copier sans mise en forme
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
